/* @flow */
import { RouterStore, syncHistoryWithStore } from 'mobx-react-router';

import createBrowserHistory from 'history/createBrowserHistory';

import AuthenticationStore from 'stores/AuthenticationStore';
import AccountStore from 'stores/AccountStore';
import ActionDrawerStore from 'stores/ActionDrawerStore';
import ManufacturersProductsStore from 'stores/ManufacturersProductsStore';
import CostCenterDetailsStore from 'stores/CostCenterDetailsStore';
import ProjectDetailsStore from 'stores/ProjectDetailsStore';
import NavigationSearchStore from 'stores/NavigationSearchStore';
import LocationChooserStore from 'stores/LocationChooserStore';
import LocationStore from 'stores/LocationStore';
import AssetStore from 'stores/AssetStore';
import ContractStore from 'stores/ContractStore';
import UserStore from 'stores/UserStore';
import UserCaaSStore from 'stores/UserCaaSStore';
import WidgetStore from 'stores/WidgetStore';
import DashboardStore from 'stores/DashboardStore';
import NavigationStore from 'stores/NavigationStore';
import NotificationStore from 'stores/NotificationStore';
import EmailDomainsStore from 'stores/EmailDomainsStore';
import KenticoStore from 'stores/KenticoStore';
import ReportViewStore from 'stores/ReportViewStore';
import BrandStore from 'stores/BrandStore';
import CustomerAccountStore from 'stores/CustomerAccountStore';
import RegionStore from 'stores/RegionStore';
import WelcomePageStore from 'stores/WelcomePageStore';

import { AccountModel, EmailModel, PasswordModel } from 'models/AccountModels';
import { Asset, Manufacturer, Product, Project } from 'models/ProductModels';

import ApiService from './ApiService';
import InlineManualService from './InlineManualService';
import ErrorHandlerService from './ErrorHandlerService';
import HotjarService from './HotjarService';

export const browserHistory = createBrowserHistory();
export const authenticationStore: AuthenticationStore = new AuthenticationStore();
export const apiService: ApiService = new ApiService(authenticationStore);
export const inlineManualService: InlineManualService = new InlineManualService();
export const routing: RouterStore = new RouterStore();
export const accountStore: AccountStore = new AccountStore(apiService);
export const costCenterDetailsStore: CostCenterDetailsStore = new CostCenterDetailsStore(
  apiService
);
export const projectDetailsStore: ProjectDetailsStore = new ProjectDetailsStore(apiService);
export const navigationSearchStore: NavigationSearchStore = new NavigationSearchStore(apiService);
export const assetStore: AssetStore = new AssetStore(apiService);
export const contractStore: ContractStore = new ContractStore(apiService);

export const actionDrawerStore = new ActionDrawerStore(apiService);
export const manufacturersProductsStore: ManufacturersProductsStore = new ManufacturersProductsStore(
  apiService
);
export const emailDomainsStore = new EmailDomainsStore(apiService);
export const accountModel = new AccountModel(apiService);
export const passwordModel = new PasswordModel(apiService);
export const emailModel = new EmailModel(apiService);
export const manufacturer = new Manufacturer();
export const notificationStore = new NotificationStore(apiService);
export const userStore = new UserStore(apiService);
export const widgetStore = new WidgetStore(apiService);
export const dashboardStore = new DashboardStore(apiService);
export const locationChooserStore = new LocationChooserStore(apiService);
export const locationStore = new LocationStore(apiService);
export const navigationStore = new NavigationStore(apiService);
export const errorHandlerService = new ErrorHandlerService();
export const kenticoStore = new KenticoStore();
export const reportViewStore = new ReportViewStore(apiService);
export const brandStore = new BrandStore(apiService);
export const customerAccountStore = new CustomerAccountStore(apiService);
export const regionStore = new RegionStore(apiService, userStore);
export const welcomePageStore = new WelcomePageStore(apiService);

export const hotjarService = new HotjarService(userStore);

/** @see github.com/alisd23/mobx-react-router/tree/v4.0.2#synchistorywithstorehistory-store */
export const mrrEnhancedHistory = syncHistoryWithStore(browserHistory, routing);

/**
 * MANUAL: Define types of Global Stores in here.
 */
export type TypeStores = {|
  apiService: ApiService,
  routing: RouterStore,
  authenticationStore: AuthenticationStore,
  loginModel: AuthenticationStore,
  passwordModel: PasswordModel,
  accountModel: AccountModel,
  emailModel: EmailModel,
  manufacturer: Manufacturer,
  project: Project,
  product: Product,
  asset: Asset,
  accountStore: AccountStore,
  costCenterDetailsStore: CostCenterDetailsStore,
  projectDetailsStore: ProjectDetailsStore,
  actionDrawerStore: ActionDrawerStore,
  manufacturersProductsStore: ManufacturersProductsStore,
  navigationSearchStore: NavigationSearchStore,
  assetStore: AssetStore,
  notificationStore: NotificationStore,
  contractStore: ContractStore,
  userStore: UserStore,
  userCaaSStore: UserCaaSStore,
  widgetStore: WidgetStore,
  dashboardStore: DashboardStore,
  locationChooserStore: LocationChooserStore,
  locationStore: LocationStore,
  navigationStore: NavigationStore,
  kenticoStore: KenticoStore,
  emailDomainsStore: EmailDomainsStore,
  reportViewStore: ReportViewStore,
  brandStore: BrandStore,
  customerAccountStore: CustomerAccountStore,
  regionStore: RegionStore,
  welcomePageStore: WelcomePageStore,
|};

/**
 * Export all available stores in the application.
 *
 * MANUAL: When new store is created and must be available via `inject` function of mobx add it here.
 */
export const getStores = (): TypeStores => ({
  apiService,
  routing,
  authenticationStore,
  accountStore,
  costCenterDetailsStore,
  projectDetailsStore,
  actionDrawerStore,
  manufacturersProductsStore: new ManufacturersProductsStore(apiService),
  navigationSearchStore,
  loginModel: authenticationStore,
  accountModel,
  passwordModel: new PasswordModel(apiService),
  emailModel: new EmailModel(apiService),
  manufacturer: new Manufacturer(),
  project: new Project(),
  product: new Product(),
  asset: new Asset(),
  assetStore,
  notificationStore,
  contractStore,
  userStore,
  userCaaSStore: new UserCaaSStore(apiService),
  widgetStore,
  dashboardStore,
  locationChooserStore,
  locationStore,
  navigationStore,
  kenticoStore,
  emailDomainsStore,
  reportViewStore,
  brandStore,
  customerAccountStore,
  regionStore,
  welcomePageStore: new WelcomePageStore(apiService),
});

export const getStore = types => {
  if (types) {
    const stores = types.map(key => getStores()[key]);

    if (stores.length) {
      return stores;
    }
  }

  return [];
};
